import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { DEFAULT_TEAM_ID, TEAMS_BY_ID, themeFor, themeToCssVars, type Team, type Theme } from '@shared/teams'

interface ThemeCtx {
  theme: Theme
  team: Team
  teamId: string
  /**
   * Paint the app in a team's colours before that team is anyone's team.
   *
   * Signup is the one place this is needed: on /setup there is no user row yet,
   * so `teamId` below is null and the provider falls back to the cached team —
   * which means tapping a logo would change nothing until after Continue. A
   * preview is deliberately *not* written to localStorage; abandoning setup
   * shouldn't leave the next visit wearing a team nobody picked. Pass null to
   * drop back to the real team.
   */
  previewTeam: (id: string | null) => void
}

const Ctx = createContext<ThemeCtx | null>(null)

const LS_TEAM = 'sfg.team'
const LS_SIZE = 'sfg.textSize'

/** Read the last known team before the first fetch, so there's no colour flash. */
export function cachedTeamId(): string {
  try { return localStorage.getItem(LS_TEAM) ?? DEFAULT_TEAM_ID } catch { return DEFAULT_TEAM_ID }
}

export function ThemeProvider({
  teamId,
  textSize = 'normal',
  children,
}: {
  teamId: string | null | undefined
  textSize?: 'normal' | 'large'
  children: ReactNode
}) {
  const [preview, setPreview] = useState<string | null>(null)
  const previewTeam = useCallback((next: string | null) => {
    setPreview(next && TEAMS_BY_ID[next] ? next : null)
  }, [])

  const real = teamId && TEAMS_BY_ID[teamId] ? teamId : cachedTeamId()
  const id = preview ?? real
  const theme = useMemo(() => themeFor(id), [id])
  const team = TEAMS_BY_ID[id] ?? TEAMS_BY_ID[DEFAULT_TEAM_ID]

  useEffect(() => {
    const root = document.documentElement
    for (const [k, v] of Object.entries(themeToCssVars(theme))) root.style.setProperty(k, v)
    root.setAttribute('data-team', team.abbr)
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', theme.bg)
    // Only a committed team is remembered — see `previewTeam` above.
    if (!preview) { try { localStorage.setItem(LS_TEAM, id) } catch { /* ignore */ } }
  }, [theme, team.abbr, id, preview])

  useEffect(() => {
    document.documentElement.setAttribute('data-size', textSize)
    try { localStorage.setItem(LS_SIZE, textSize) } catch { /* ignore */ }
  }, [textSize])

  const value = useMemo<ThemeCtx>(
    () => ({ theme, team, teamId: id, previewTeam }),
    [theme, team, id, previewTeam],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useTheme(): ThemeCtx {
  const v = useContext(Ctx)
  if (!v) {
    const id = cachedTeamId()
    return { theme: themeFor(id), team: TEAMS_BY_ID[id], teamId: id, previewTeam: () => {} }
  }
  return v
}

/** Apply the cached theme synchronously, before React mounts. */
export function bootstrapTheme(): void {
  const id = cachedTeamId()
  const theme = themeFor(id)
  const root = document.documentElement
  for (const [k, v] of Object.entries(themeToCssVars(theme))) root.style.setProperty(k, v)
  root.setAttribute('data-team', TEAMS_BY_ID[id]?.abbr ?? 'SF')
  try {
    const size = localStorage.getItem(LS_SIZE)
    if (size) root.setAttribute('data-size', size)
  } catch { /* ignore */ }
}
